import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { StockChecklist, SKIP_REASON_LABEL, stockChecklistPdfTrail } from "@/types/stockChecklist";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus, 
  ClipboardList, 
  Link2, 
  Copy, 
  FileText, 
  Loader2,
  Calendar,
  CheckCircle2,
  Clock,
  FileEdit,
  Trash2,
  Warehouse,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { PeriodSelector } from "@/components/Shared/PeriodSelector";
import { ptBR } from "date-fns/locale";
import { CreateChecklistDialog } from "./CreateChecklistDialog";
import type { StockChecklistItem as StockChecklistItemType } from "@/types/stockChecklist";
import { generateStockChecklistPDF } from "@/utils/pdfGenerator";
import type { StockChecklistDocumentProps } from "@/utils/pdfGenerator";
import { todayLocalStr } from "@/utils/dateHelpers";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface StockChecklistWithProgress extends Omit<StockChecklist, 'status'> {
  status: string;
  is_locked?: boolean;
  progress?: { checked: number; total: number };
  deposit_name?: string | null;
}

interface ChecklistStatusResult {
  success: boolean;
  status?: string;
}

const SKIP_LABEL_BY_ADJUSTMENT: Record<string, string> = {
  nao_acertado_esperado_negativo: SKIP_REASON_LABEL.esperado_negativo,
  nao_acertado_cadastro_diverge:  SKIP_REASON_LABEL.cadastro_diverge,
};

export const StockChecklistManager = () => {
  const { company, userRole } = useAuth();
  // A7a-i (D-A7.4): excluir conferência cancelada só master ou master 2
  // (o tipo do front ainda não tem master_2 — fila E-MASTER2-TELA; o banco é quem decide)
  const isMaster = (['master', 'master_2'] as string[]).includes(userRole ?? '');
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [pdfGeneratingId, setPdfGeneratingId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState<StockChecklistWithProgress | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [checklistToCancel, setChecklistToCancel] = useState<StockChecklistWithProgress | null>(null);
  // m-CONF.2: filtros da lista (no navegador, sobre a lista da empresa)
  const [statusFilter, setStatusFilter] = useState<'todas' | 'abertas' | 'concluidas' | 'canceladas'>('todas');
  const [depositFilter, setDepositFilter] = useState<string>('todos'); // 'todos' | 'sem' | deposit_id
  // m-CONF.21: período da criação; abre no mês corrente, inclusive para as abertas (Diretor, 19/10)
  const [periodStart, setPeriodStart] = useState<Date | undefined>(() => startOfMonth(new Date()));
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>(() => endOfMonth(new Date()));
  const [periodKey, setPeriodKey] = useState(0);
  // m-CONF.19: impacto estimado no PDF de conferência aberta, só com o botão ligado (desligado por padrão, Diretor 19/10)
  const [showEstimate, setShowEstimate] = useState(false);

  const { data: checklists, isLoading } = useQuery({
    queryKey: ['stock-checklists', company?.id],
    queryFn: async () => {
      // A7a-i: progresso contado no banco (conferência do depósito inteiro passa de 1.000 itens)
      const [listRes, progressRes] = await Promise.all([
        supabase
          .from('stock_checklists')
          .select('*, deposit:deposits(name)')
          .eq('company_id', company!.id)
          .order('created_at', { ascending: false }),
        supabase.rpc('get_stock_checklists_progress'),
      ]);

      if (listRes.error) throw listRes.error;
      if (progressRes.error) throw progressRes.error;

      const progressById = new Map(
        (progressRes.data ?? []).map((p) => [p.checklist_id, { total: p.total, checked: p.checked }])
      );

      return (listRes.data || []).map(checklist => {
        const deposit = (checklist as { deposit?: { name: string } | null }).deposit;
        return {
          ...checklist,
          filters: checklist.filters as StockChecklistWithProgress['filters'],
          deposit: undefined,
          deposit_name: deposit?.name ?? null,
          progress: progressById.get(checklist.id) ?? { total: 0, checked: 0 },
        } as StockChecklistWithProgress;
      });
    },
    enabled: !!company?.id,
    staleTime: 5 * 60 * 1000,
  });

  const cancelMutation = useMutation({
    // A7a-i: cancelar pelo banco (chave de conferência ou master); o banco grava o registro de auditoria
    mutationFn: async (checklist: StockChecklistWithProgress) => {
      const { data, error } = await supabase.rpc('set_stock_checklist_status', {
        p_checklist_id: checklist.id,
        p_action: 'cancel',
      });
      if (error) throw error;
      if (!(data as unknown as ChecklistStatusResult)?.success) throw new Error('Erro ao cancelar conferência');
    },
    onSuccess: () => {
      toast.success('Conferência cancelada com sucesso');
      queryClient.invalidateQueries({ queryKey: ['stock-checklists'] });
    },
    onError: (err) => {
      console.error('Erro ao cancelar conferência:', err);
      toast.error((err as { message?: string })?.message || 'Erro ao cancelar conferência');
    },
    onSettled: () => {
      setShowCancelDialog(false);
      setChecklistToCancel(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (checklist: StockChecklistWithProgress) => {
      if (checklist.status === 'completed' || checklist.is_locked) {
        throw new Error('Conferência protegida contra exclusão');
      }

      // A7a-i: excluir pelo banco (só cancelada, só master); itens saem em cascata e o banco grava a auditoria
      const { data, error } = await supabase.rpc('set_stock_checklist_status', {
        p_checklist_id: checklist.id,
        p_action: 'delete',
      });
      if (error) throw error;
      if (!(data as unknown as ChecklistStatusResult)?.success) throw new Error('Erro ao excluir conferência');
    },
    onSuccess: () => {
      toast.success('Conferência excluída com sucesso');
      queryClient.invalidateQueries({ queryKey: ['stock-checklists'] });
    },
    onError: (err) => {
      console.error('Erro ao excluir conferência:', err);
      toast.error((err as { message?: string })?.message || 'Erro ao excluir conferência');
    },
    onSettled: () => {
      setShowDeleteDialog(false);
      setChecklistToDelete(null);
    }
  });

  // A8-i: confirmar os custos cadastrados da parte inteira (só master; o banco recusa fora de balanço aberto)
  const confirmCostsMutation = useMutation({
    mutationFn: async (checklist: StockChecklistWithProgress) => {
      // types.ts só é regenerado depois do APPLY do A8-i: cast documentado
      const { data, error } = await (supabase.rpc as any)('confirm_stock_checklist_registered_costs', {
        p_checklist_id: checklist.id,
      });
      if (error) throw error;
      return (data as { confirmed?: number } | null)?.confirmed ?? 0;
    },
    onSuccess: (confirmed) => {
      toast.success(confirmed > 0 ? `${confirmed} custo(s) cadastrado(s) confirmado(s)` : 'Nenhum custo cadastrado a confirmar');
    },
    onError: (err) => {
      console.error('Erro ao confirmar custos:', err);
      toast.error((err as { message?: string })?.message || 'Erro ao confirmar custos');
    },
  });

  const copyLink = (checklist: StockChecklistWithProgress) => {
    const link = `${window.location.origin}/conferencia/${checklist.access_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const openLink = (checklist: StockChecklistWithProgress) => {
    const link = `${window.location.origin}/conferencia/${checklist.access_token}`;
    window.open(link, '_blank');
  };

  const handleGenerateReport = async (checklist: StockChecklistWithProgress) => {
    if (pdfGeneratingId) return;
    try {
      setPdfGeneratingId(checklist.id);

      // A7b (m-CONF.5): a fonte do PDF é a leitura da conferência no banco (pede a chave), com quem contou,
      // item achado, origem do custo, custo aplicado e custo médio depois da finalização
      const { data: result, error } = await supabase.rpc('get_stock_checklist_by_token', {
        p_token: checklist.access_token!,
      });
      if (error) throw error;
      const read = result as unknown as {
        success: boolean;
        error?: string;
        checklist?: { name: string; created_at: string; completed_at?: string | null; filters?: unknown };
        deposit?: { name: string } | null;
        items?: StockChecklistItemType[];
        financial_impact?: number;
        estimated_impact?: number | null;
      };
      if (!read.success || !read.checklist) throw new Error(read.error ?? 'Erro ao ler a conferência');
      const data = read.checklist;
      const items = read.items ?? [];
      const checkedItems = items.filter((i) => i.checked);

      const hasPriceEdits = checkedItems.some((i) => i.price_edited_by_user);
      const hasCostEdits  = checkedItems.some((i) => i.cost_edited_by_user);
      const skipLabel = (i: StockChecklistItemType) =>
        i.adjustment_type ? SKIP_LABEL_BY_ADJUSTMENT[i.adjustment_type] ?? null : null;

      const divergentItems = checkedItems.filter(
        (i) => i.difference != null && i.difference !== 0 && !skipLabel(i)
      );
      const positiveItems  = divergentItems.filter((i) => (i.difference ?? 0) > 0);
      const negativeItems  = divergentItems.filter((i) => (i.difference ?? 0) < 0);

      // m-CONF.19: finalizada = valor que os acertos lançaram no estoque (o mesmo da finalização); aberta = nada lançado,
      // e a estimativa (regra de custo da finalização) só com o botão "Mostrar impacto estimado" ligado
      const isOpen = ['draft', 'in_progress'].includes(checklist.status);
      const financialImpactKind: 'lancado' | 'estimado' = isOpen ? 'estimado' : 'lancado';
      const financialImpact = isOpen
        ? (showEstimate ? Number(read.estimated_impact ?? 0) : 0)
        : Number(read.financial_impact ?? 0);

      const filters = data.filters as { categories?: string[]; abc_curves?: string[]; letterFilter?: string[] } | null;
      const filterParts: string[] = [];
      if (filters?.categories?.length) filterParts.push(`Categorias: ${filters.categories.join(', ')}`);
      if (filters?.abc_curves?.length) filterParts.push(`Curva ABC: ${filters.abc_curves.join(', ')}`);
      if (filters?.letterFilter?.length) filterParts.push(`Letra: ${filters.letterFilter.join(', ')}`);

      const docData: StockChecklistDocumentProps = {
        company: {
          name:    company?.name ?? '',
          logo_url: company?.logo_url ?? undefined,
        },
        checklist: {
          name:               data.name,
          created_at:         data.created_at ?? new Date().toISOString(),
          completed_at:       data.completed_at ?? undefined,
          deposit_name:       read.deposit?.name ?? null,
          completed_by_name:  null,
          filters_summary:    filterParts.length ? filterParts.join(' | ') : null,
        },
        stats: {
          total:          items.length,
          checked:        checkedItems.length,
          divergent:      divergentItems.length,
          positiveCount:  positiveItems.length,
          negativeCount:  negativeItems.length,
          positiveQty:    positiveItems.reduce((s, i) => s + (i.difference ?? 0), 0),
          negativeQty:    Math.abs(negativeItems.reduce((s, i) => s + (i.difference ?? 0), 0)),
          pricesEdited:   checkedItems.filter((i) => i.price_edited_by_user || i.cost_edited_by_user).length,
          photos:         checkedItems.filter((i) => !!i.image_url).length,
          financialImpact,
          financialImpactKind,
        },
        items: checkedItems.map((i) => ({
          sku:                  i.sku ?? undefined,
          name:                 i.name ?? '',
          category:             i.category ?? undefined,
          unit_name:            i.unit_name ?? undefined,
          current_stock:        i.system_stock_at_count ?? i.current_stock,
          counted_stock:        i.counted_stock,
          difference:           i.difference,
          notes:                i.notes,
          sale_price:           i.sale_price,
          system_sale_price:    i.system_sale_price,
          unit_cost:            i.unit_cost,
          system_cost_price:    i.system_cost_price,
          price_edited_by_user: i.price_edited_by_user ?? false,
          cost_edited_by_user:  i.cost_edited_by_user  ?? false,
          skip_label:           skipLabel(i),
          ...stockChecklistPdfTrail(i),
        })),
        showSalePriceColumn: hasPriceEdits,
        showCostPriceColumn: hasCostEdits,
        showSkipColumn:      checkedItems.some((i) => !!skipLabel(i)),
        showCountedByColumn: checkedItems.some((i) => !!i.checked_by_name),
        showCostTrailColumns: checkedItems.some((i) => !!i.cost_origin),
      };

      const blob = await generateStockChecklistPDF(docData);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const safeName = data.name.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const dateStr  = todayLocalStr(); // m-CONF.18: dia de São Paulo, não UTC
      a.href     = url;
      a.download = `conferencia-${safeName}-${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF gerado e baixado com sucesso!');
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      toast.error((err as { message?: string })?.message || 'Erro ao gerar PDF da conferência');
    } finally {
      setPdfGeneratingId(null);
    }
  };

  const handleDeleteChecklist = (checklist: StockChecklistWithProgress) => {
    setChecklistToDelete(checklist);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!checklistToDelete) return;
    deleteMutation.mutate(checklistToDelete);
  };

  const handleCancelChecklist = (checklist: StockChecklistWithProgress) => {
    setChecklistToCancel(checklist);
    setShowCancelDialog(true);
  };

  const confirmCancel = () => {
    if (!checklistToCancel) return;
    cancelMutation.mutate(checklistToCancel);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline"><FileEdit className="h-3 w-3 mr-1" /> Rascunho</Badge>;
      case 'in_progress':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Em Progresso</Badge>;
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Concluído</Badge>;
      case 'cancelled':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Cancelado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // m-CONF.9: o filtro lista os depósitos ativos da empresa (mesma leitura do diálogo de criação), não só os que já têm
  // conferência
  const { data: activeDeposits } = useQuery({
    queryKey: ['stock-checklist-deposits', company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deposits')
        .select('id, name')
        .eq('company_id', company!.id)
        .eq('active', true)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  // m-CONF.2: opções de depósito (pelo id; rótulo pelo nome) e lista filtrada — antes do retorno de carregamento
  // m-CONF.9: depósitos ativos + os de conferências listadas que não estejam ativos + "Sem depósito" se houver
  const depositOptions = useMemo(() => {
    const byId = new Map<string, string>();
    (activeDeposits ?? []).forEach((d) => byId.set(d.id, d.name));
    let hasNone = false;
    (checklists ?? []).forEach((c) => {
      if (c.deposit_id) { if (!byId.has(c.deposit_id)) byId.set(c.deposit_id, c.deposit_name ?? c.deposit_id); }
      else hasNone = true;
    });
    const list = Array.from(byId, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    return { list, hasNone };
  }, [checklists, activeDeposits]);

  const filteredChecklists = useMemo(() => {
    return (checklists ?? []).filter((c) => {
      if (statusFilter === 'abertas' && c.status !== 'draft' && c.status !== 'in_progress') return false;
      if (statusFilter === 'concluidas' && c.status !== 'completed') return false;
      if (statusFilter === 'canceladas' && c.status !== 'cancelled') return false;
      if (depositFilter === 'sem' && c.deposit_id) return false;
      if (depositFilter !== 'todos' && depositFilter !== 'sem' && c.deposit_id !== depositFilter) return false;
      // m-CONF.21: período pelo dia local da criação (o fim do personalizado vem à meia-noite); cada ponta vale sozinha
      if (periodStart || periodEnd) {
        const day = format(new Date(c.created_at), 'yyyy-MM-dd');
        if (periodStart && day < format(periodStart, 'yyyy-MM-dd')) return false;
        if (periodEnd && day > format(periodEnd, 'yyyy-MM-dd')) return false;
      }
      return true;
    });
  }, [checklists, statusFilter, depositFilter, periodStart, periodEnd]);

  const isCurrentMonth =
    !!periodStart && !!periodEnd &&
    format(periodStart, 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
    format(periodEnd, 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd');
  const filtersActive = statusFilter !== 'todas' || depositFilter !== 'todos' || !isCurrentMonth;
  const clearFilters = () => {
    setStatusFilter('todas');
    setDepositFilter('todos');
    setPeriodStart(startOfMonth(new Date()));
    setPeriodEnd(endOfMonth(new Date()));
    setPeriodKey((k) => k + 1); // remonta o seletor para o rótulo voltar a "Este mês"
  };

  if (isLoading && !checklists) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const checklistList = checklists || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Conferência de Estoque</h2>
          <p className="text-muted-foreground">Crie e gerencie conferências de estoque</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Conferência
        </Button>
      </div>

      {checklistList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhuma conferência criada</h3>
            <p className="text-muted-foreground mb-4">
              Crie uma conferência para começar a verificar seu estoque
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Conferência
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
        {/* m-CONF.2: filtros por status, depósito e período da criação */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Status</span>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="abertas">Abertas</SelectItem>
                <SelectItem value="concluidas">Concluídas</SelectItem>
                <SelectItem value="canceladas">Canceladas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Depósito</span>
            <Select value={depositFilter} onValueChange={setDepositFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {depositOptions.list.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
                {depositOptions.hasNone && <SelectItem value="sem">Sem depósito</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          {/* m-CONF.21: seletor de período padrão do sistema, abrindo no mês corrente */}
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Período</span>
            <PeriodSelector
              key={periodKey}
              startDate={periodStart}
              endDate={periodEnd}
              onPeriodChange={(start, end) => { setPeriodStart(start); setPeriodEnd(end); }}
            />
          </div>
          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>Limpar filtros</Button>
          )}
          <div className="flex items-center gap-2 pb-2">
            <Switch id="mostrar-impacto-estimado" checked={showEstimate} onCheckedChange={setShowEstimate} />
            <Label htmlFor="mostrar-impacto-estimado" className="text-sm font-normal">
              Mostrar impacto estimado
            </Label>
          </div>
          <span className="text-sm text-muted-foreground ml-auto">
            {filteredChecklists.length} de {checklistList.length} conferências
          </span>
        </div>

        {filteredChecklists.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Nenhuma conferência com esses filtros.
            </CardContent>
          </Card>
        ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredChecklists.map(checklist => (
            <Card key={checklist.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{checklist.name}</CardTitle>
                    {checklist.description && (
                      <CardDescription className="line-clamp-2">
                        {checklist.description}
                      </CardDescription>
                    )}
                  </div>
                  {getStatusBadge(checklist.status)}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {format(new Date(checklist.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Warehouse className="h-4 w-4" />
                  <span>{checklist.deposit_name ? `Depósito ${checklist.deposit_name}` : 'Sem depósito (conferência antiga)'}</span>
                </div>
                {checklist.balance_id && (
                  <Badge variant="outline" className="text-xs">Parte de balanço</Badge>
                )}

                {checklist.progress && checklist.progress.total > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progresso</span>
                      <span className="font-medium">
                        {checklist.progress.checked} / {checklist.progress.total}
                      </span>
                    </div>
                    <Progress 
                      value={(checklist.progress.checked / checklist.progress.total) * 100} 
                      className="h-2" 
                    />
                  </div>
                )}

                <div className="flex gap-2 pt-2 flex-wrap">
                  {(checklist.status === 'draft' || checklist.status === 'in_progress') && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openLink(checklist)}
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Abrir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyLink(checklist)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {isMaster && checklist.balance_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => confirmCostsMutation.mutate(checklist)}
                          disabled={confirmCostsMutation.isPending}
                        >
                          Confirmar cadastrados
                        </Button>
                      )}
                    </>
                  )}
                  {/* m-CONF.19 (Diretor 21/10, "1a"): PDF também da conferência aberta — com "Mostrar impacto estimado"
                      ligado traz o quadro estimado; desligado, sem quadro. Cancelada segue sem PDF. */}
                  {(checklist.status === 'completed' || checklist.status === 'draft' || checklist.status === 'in_progress') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGenerateReport(checklist)}
                      disabled={pdfGeneratingId === checklist.id}
                    >
                      {pdfGeneratingId === checklist.id
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <FileText className="h-4 w-4 mr-1" />
                      }
                      PDF
                    </Button>
                  )}
                  {/* Active: cancel (preserves history). Completed: no action. Cancelled: delete. */}
                  {(checklist.status === 'draft' || checklist.status === 'in_progress') ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelChecklist(checklist)}
                      disabled={cancelMutation.isPending}
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  ) : checklist.status === 'cancelled' && isMaster ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteChecklist(checklist)}
                      disabled={deleteMutation.isPending}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      {/* m-CONF.10: rótulo "Excluir" junto do ícone (Diretor 19/10, opção 1; regra D-A7.4 sem mudança) */}
                      <Trash2 className="h-4 w-4 mr-1" />
                      Excluir
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
        </>
      )}

      <CreateChecklistDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['stock-checklists'] });
          setShowCreateDialog(false);
        }}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Conferência?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todos os itens e dados 
              da conferência "{checklistToDelete?.name}" serão removidos permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Conferência?</AlertDialogTitle>
            <AlertDialogDescription>
              A conferência "{checklistToCancel?.name}" será marcada como cancelada.
              Os dados serão preservados no histórico, mas não será possível retomá-la.
              Os produtos dela ficam livres para outra conferência do mesmo depósito.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelMutation.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              disabled={cancelMutation.isPending}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelando...
                </>
              ) : (
                'Cancelar Conferência'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
